import {
  type AdminBooking,
  type BookingOutcomeRecordRequest,
  type BookingStaffCancelRequest,
  type BookingsListQuerySchema,
  type Paginated,
  type PresignedUpload,
  type Venue,
  type VenueCity,
  type VenueCreateRequest,
  type VenueDetail,
  type VenuePhotoAttachRequest,
  type VenuePhotoOrderRequest,
  type VenuePhotoPresignRequest,
  type VenueSlot,
  type VenueSlotBatchRequest,
  type VenueSlotBatchResult,
  type VenueSlotCreateRequest,
  type VenueSlotUpdateRequest,
  type VenueSlotsListQuerySchema,
  type VenueUpdateRequest,
  type VenuesListQuerySchema,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contract's own INPUT shape (U3) — never a hand-written copy of the filters. */
type VenuesParams = z.input<typeof VenuesListQuerySchema>;

/**
 * The window, plus the venue that owns it.
 *
 * `venueId` addresses the route and is destructured out before the rest becomes
 * query parameters — sending it as a filter as well would be a second way to say
 * the same thing, and the api accepts only one.
 */
type VenueSlotsParams = z.input<typeof VenueSlotsListQuerySchema> & { venueId: string };

/**
 * The staff oversight list's parameters (P25d2).
 *
 * `from`/`to` are **instants**, not day keys: the api casts them against a
 * `timestamp without time zone`, so a `YYYY-MM-DD` arrives as midnight in a zone
 * nobody chose and answers about the wrong day. The `date` filter writes a day
 * key into the URL, and `shared/utils/day-range` is what turns it into this.
 */
type BookingsParams = z.input<typeof BookingsListQuerySchema>;

/**
 * Partner venues, and (from P25b/P25c) the slots and bookings that hang off one.
 *
 * ## One api for the whole module, not one per resource
 *
 * A slot belongs to a venue and a booking names both, so the reactive edges will
 * cross all three — and one api's `invalidatesTags` cannot reach another's cache
 * (P17c). Getting this right in the phase that has only venues is what stops
 * P25b creating a second root that then cannot invalidate this one.
 *
 * ## Every write invalidates the city facet too
 *
 * `VenueCities` is not decoration. Saving the first venue in a new city adds an
 * option to the filter the operator is looking at, and withdrawing the last one
 * takes it away — a stale facet is how an operator's own new city fails to
 * appear in the dropdown they just used.
 *
 * ## A write returns the row and the screen still re-reads
 *
 * `POST`/`PATCH` answer with the venue, and writing that into the cache would
 * save a round trip. Deliberately not done, for the reason every other api in
 * this portal records: rendering from a mutation response gives the screen two
 * sources for one row, and they disagree the moment a colleague saves.
 */
export const meetingsApi = createApi({
  reducerPath: 'meetingsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Venues', 'Venue', 'VenueCities', 'VenueSlots', 'Bookings', 'Booking'],
  endpoints: (builder) => ({
    /**
     * The venue list.
     *
     * `VenuesService.list` goes through `paginateFilteredLocalized`, so `search`
     * reaches the translated name and city across **both** locales as well as
     * the staff-facing contact name. A row found by its Arabic name still
     * renders in the locale being read — which is what the untitled fallback in
     * `venue-label` is for, rather than a defect.
     */
    listVenues: builder.query<Paginated<Venue>, VenuesParams>({
      query: (params) => ({ url: '/admin/venues', method: 'GET', params }),
      providesTags: ['Venues'],
    }),

    /** Every city in the directory — the list's filter options, in one read. */
    venueCities: builder.query<VenueCity[], void>({
      query: () => ({ url: '/admin/venues/cities', method: 'GET' }),
      providesTags: ['VenueCities'],
    }),

    /** ALL locales — what the ar/en tabs prefill from (W7). */
    venue: builder.query<VenueDetail, string>({
      query: (id) => ({ url: `/admin/venues/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Venue' as const, id }],
    }),

    createVenue: builder.mutation<VenueDetail, VenueCreateRequest>({
      query: (body) => ({ url: '/admin/venues', method: 'POST', body }),
      invalidatesTags: ['Venues', 'VenueCities'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        await announceVenueChange(queryFulfilled.then(({ data }) => data.id));
      },
    }),

    updateVenue: builder.mutation<VenueDetail, { id: string; body: VenueUpdateRequest }>({
      query: ({ id, body }) => ({ url: `/admin/venues/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Venue' as const, id },
        'Venues' as const,
        'VenueCities' as const,
      ],
      async onQueryStarted({ id }, { queryFulfilled }) {
        await announceVenueChange(queryFulfilled.then(() => id));
      },
    }),

    deleteVenue: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/venues/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Venue' as const, id },
        'Venues' as const,
        'VenueCities' as const,
      ],
      async onQueryStarted(_id, { queryFulfilled }) {
        // A 204 names nothing, which is what the null on the event is for.
        await announceVenueChange(queryFulfilled.then(() => null));
      },
    }),

    /**
     * Step 1 of adding a photograph: a constrained presigned PUT.
     *
     * The body carries only what is being sent — the server pins the kind, so a
     * `meetings.manage` operator cannot mint an upload slot for anything else.
     * No tag: nothing has changed until the attach lands.
     */
    presignVenuePhoto: builder.mutation<
      PresignedUpload,
      { venueId: string; body: VenuePhotoPresignRequest }
    >({
      query: ({ venueId, body }) => ({
        url: `/admin/venues/${venueId}/photos/presign-upload`,
        method: 'POST',
        body,
      }),
    }),

    /** Step 3: finalize the object and bind it, as one call. */
    attachVenuePhoto: builder.mutation<
      VenueDetail,
      { venueId: string; body: VenuePhotoAttachRequest }
    >({
      query: ({ venueId, body }) => ({
        url: `/admin/venues/${venueId}/photos`,
        method: 'POST',
        body,
      }),
      invalidatesTags: photoTags,
    }),

    /**
     * The whole arrangement in one body — never "move this one to position 3",
     * which the server would evaluate against whatever it holds now.
     */
    reorderVenuePhotos: builder.mutation<
      VenueDetail,
      { venueId: string; body: VenuePhotoOrderRequest }
    >({
      query: ({ venueId, body }) => ({
        url: `/admin/venues/${venueId}/photos/order`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: photoTags,
    }),

    detachVenuePhoto: builder.mutation<VenueDetail, { venueId: string; mediaAssetId: string }>({
      query: ({ venueId, mediaAssetId }) => ({
        url: `/admin/venues/${venueId}/photos/${mediaAssetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: photoTags,
    }),

    /**
     * Every slot the window touches — the availability calendar's one read.
     *
     * A **window, not a page**: the grid draws the whole month it is showing, so
     * a `pageSize` would make it stop silently partway through and an operator
     * would author a clash into a gap that is not there. The width is bounded by
     * the contract (`VENUE_SLOT_WINDOW_MAX_DAYS`), which a six-week grid always
     * fits inside.
     */
    venueSlots: builder.query<VenueSlot[], VenueSlotsParams>({
      query: ({ venueId, ...params }) => ({
        url: `/admin/venues/${venueId}/slots`,
        method: 'GET',
        params,
      }),
      providesTags: slotTags,
    }),

    createVenueSlot: builder.mutation<VenueSlot, { venueId: string; body: VenueSlotCreateRequest }>(
      {
        query: ({ venueId, body }) => ({
          url: `/admin/venues/${venueId}/slots`,
          method: 'POST',
          body,
        }),
        invalidatesTags: slotTags,
      },
    ),

    /**
     * A whole generated recurrence, in one act.
     *
     * The body carries the **expanded** slots rather than the rule that produced
     * them (`slot-recurrence.util` does the expanding), and the reply says what
     * was created *and* what was passed over — "nineteen of your twenty-four" is
     * a fact the dialog has to be able to state.
     */
    createVenueSlotBatch: builder.mutation<
      VenueSlotBatchResult,
      { venueId: string; body: VenueSlotBatchRequest }
    >({
      query: ({ venueId, body }) => ({
        url: `/admin/venues/${venueId}/slots/batch`,
        method: 'POST',
        body,
      }),
      invalidatesTags: slotTags,
    }),

    updateVenueSlot: builder.mutation<
      VenueSlot,
      { venueId: string; slotId: string; body: VenueSlotUpdateRequest }
    >({
      query: ({ venueId, slotId, body }) => ({
        url: `/admin/venues/${venueId}/slots/${slotId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: slotTags,
    }),

    deleteVenueSlot: builder.mutation<void, { venueId: string; slotId: string }>({
      query: ({ venueId, slotId }) => ({
        url: `/admin/venues/${venueId}/slots/${slotId}`,
        method: 'DELETE',
      }),
      invalidatesTags: slotTags,
    }),

    /**
     * Staff oversight of every booking on the platform (P25d2).
     *
     * **No `search`.** `BOOKING_FILTER_SPEC` whitelists none, and its docblock
     * says why: a booking carries no text a person searches, and the facts one
     * would search — the venue's name, the members' identities — live in other
     * modules. A box that narrows nothing is the control that lies, so the
     * surface must not offer one either.
     */
    listBookings: builder.query<Paginated<AdminBooking>, BookingsParams>({
      query: (params) => ({ url: '/admin/bookings', method: 'GET', params }),
      providesTags: ['Bookings'],
    }),

    booking: builder.query<AdminBooking, string>({
      query: (bookingId) => ({ url: `/admin/bookings/${bookingId}`, method: 'GET' }),
      providesTags: bookingTag,
    }),

    /**
     * What happened at a meeting that was actually scheduled.
     *
     * Refused on anything else with `booking_outcome_not_applicable`, so the
     * surface offers it only while the booking is `scheduled` — the api is the
     * authority, and the control agreeing with it is what stops the operator
     * meeting a refusal they could not have predicted.
     */
    recordBookingOutcome: builder.mutation<
      AdminBooking,
      { bookingId: string; body: BookingOutcomeRecordRequest }
    >({
      query: ({ bookingId, body }) => ({
        url: `/admin/bookings/${bookingId}/outcome`,
        method: 'POST',
        body,
      }),
      invalidatesTags: bookingWriteTags,
    }),

    /**
     * Staff calling off a booking two members had already agreed.
     *
     * A `POST`, not a `DELETE`: nothing is deleted — `cancelled` is a status,
     * and the row stays the record of what two people were told out loud. The
     * reason is **required** by the contract, which is why this is a form and
     * not a confirmation (`BookingStaffCancelSchema`).
     */
    cancelBooking: builder.mutation<
      AdminBooking,
      { bookingId: string; body: BookingStaffCancelRequest }
    >({
      query: ({ bookingId, body }) => ({
        url: `/admin/bookings/${bookingId}/cancel`,
        method: 'POST',
        body,
      }),
      invalidatesTags: bookingWriteTags,
    }),
  }),
});

/**
 * A photograph write changes the venue's own detail and **nothing else**.
 *
 * Deliberately not `Venues` or `VenueCities`: the list draws no photograph and
 * the city facet counts venues, so invalidating either would refetch two queries
 * to redraw a grid neither renders. It is also why `venues.updated_at` is left
 * alone by these routes — `formKey` is built from it, and bumping it would
 * discard an operator's unsaved form edits every time they added a picture.
 */
function photoTags(
  _result: unknown,
  _error: unknown,
  { venueId }: { venueId: string },
): [{ type: 'Venue'; id: string }] {
  // A `function` rather than a `const` arrow: `createApi` above evaluates its
  // endpoint definitions immediately, so a `const` declared below would still be
  // in its temporal dead zone and the module would throw at import.
  return [{ type: 'Venue' as const, id: venueId }];
}

/**
 * A slot write changes the venue's calendar and **nothing else**.
 *
 * Deliberately not `Venue`, `Venues` or `VenueCities`, for the reason
 * {@link photoTags} records: neither the detail page nor the list draws a slot,
 * and bumping `Venue` would discard an operator's unsaved venue-form edits every
 * time they opened a window (`formKey` is built from `updatedAt`).
 *
 * No client event either (W5), and that is a decision rather than an omission:
 * nothing outside this module reads slots until P25c's bookings, and same-module
 * refresh is tag invalidation by rule. The event arrives with its first listener.
 *
 * A `function` rather than a `const` arrow, like `photoTags`: `createApi`
 * evaluates its endpoint definitions immediately, so a `const` declared below
 * would still be in its temporal dead zone and the module would throw at import.
 */
function slotTags(
  _result: unknown,
  _error: unknown,
  { venueId }: { venueId: string },
): [{ type: 'VenueSlots'; id: string }] {
  return [{ type: 'VenueSlots' as const, id: venueId }];
}

/**
 * One booking's own row.
 *
 * A `function` rather than a `const` arrow, like {@link photoTags}: `createApi`
 * evaluates its endpoint definitions immediately, so a `const` declared below
 * would still be in its temporal dead zone and the module would throw at import.
 */
function bookingTag(
  _result: unknown,
  _error: unknown,
  bookingId: string,
): [{ type: 'Booking'; id: string }] {
  return [{ type: 'Booking' as const, id: bookingId }];
}

/**
 * A booking write moves the queue **and** the row it landed on.
 *
 * Both, unlike {@link slotTags}: an outcome changes the row's own status and
 * takes it out of the "upcoming" count the queue and the dashboard tile both
 * read, so refreshing one without the other leaves two numbers on screen that
 * disagree. Deliberately **not** `Venue`, `Venues` or `VenueCities` — no venue
 * screen draws a booking, and bumping `Venue` would discard an operator's
 * unsaved venue-form edits (`formKey` is built from `updatedAt`).
 *
 * **No client event** (W5), for the reason `slotTags` records and on the same
 * test: nothing outside `modules/Meetings` reads a booking. The dashboard tile
 * is this module's own component rendered from the dashboard route, so it
 * refreshes through `Bookings` like everything else. The event arrives with its
 * first listener — P25e's member surface is a different app.
 */
function bookingWriteTags(
  _result: unknown,
  _error: unknown,
  { bookingId }: { bookingId: string },
): ['Bookings', { type: 'Booking'; id: string }] {
  return ['Bookings', { type: 'Booking' as const, id: bookingId }];
}

/**
 * Publishes after the write lands (W5).
 *
 * The rejection is swallowed rather than rethrown: a refused write has nothing
 * to announce, and `onQueryStarted` must never emit an unhandled rejection —
 * the component's own `await` is what surfaces the failure to the operator.
 */
async function announceVenueChange(landed: Promise<string | null>): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'meetings.venue.changed', venueId: await landed });
  } catch {
    /* refused — nothing to announce */
  }
}

export const {
  useListVenuesQuery,
  useVenueCitiesQuery,
  useVenueQuery,
  useCreateVenueMutation,
  useUpdateVenueMutation,
  useDeleteVenueMutation,
  usePresignVenuePhotoMutation,
  useAttachVenuePhotoMutation,
  useReorderVenuePhotosMutation,
  useDetachVenuePhotoMutation,
  useVenueSlotsQuery,
  useCreateVenueSlotMutation,
  useCreateVenueSlotBatchMutation,
  useUpdateVenueSlotMutation,
  useDeleteVenueSlotMutation,
  useListBookingsQuery,
  useBookingQuery,
  useRecordBookingOutcomeMutation,
  useCancelBookingMutation,
} = meetingsApi;
